import { useQuery } from "@apollo/client/react";
import { Card } from "@mui/material";
import { EditorFormWithMutation } from "../../../../pages/keys/KeyBrowser";

import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAccountContext } from "../../../../strict/app/AccountResolver";
import { CredentialDocument } from "../../../../graphql/types";
import type { CredentialQuery, CredentialQueryVariables } from "../../../../graphql/types";
import { BreadcrumbsInformation, KeyPageHeader } from "../../components/KeyPageHeader";
import { KeyPageWrapper } from "../../components/KeyPageWrapper";

const EditKey = () => {
  const [searchParams] = useSearchParams();
  const keyName = searchParams.get("keyName") || "";

  const { urlName: url_name } = useAccountContext();

  const navigate = useNavigate();

  const onCompletedEditCredential = useCallback(
    (credentialName: string) => {
      toast.success(`${credentialName} was successfully updated!`);

      navigate(`/app/${url_name}/keys`);
    },
    [navigate, url_name]
  );

  useEffect(() => {
    const handleOauthSuccess = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.message === "oauthSuccess") {
        onCompletedEditCredential(event.data.credential_name);
      }
    };

    window.addEventListener("message", handleOauthSuccess);

    return () => {
      window.removeEventListener("message", handleOauthSuccess);
    };
  }, [onCompletedEditCredential]);

  if (!keyName) {
    navigate("/keys");
  }

  const { data, error } = useQuery<CredentialQuery, CredentialQueryVariables>(CredentialDocument, {
    variables: {
      name: keyName,
    },
  });

  useEffect(() => {
    if (error) navigate("/keys");
  }, [error, navigate]);

  if (!data?.credential) return null;

  const breadcrumbs: BreadcrumbsInformation[] = [
    {
      name: "Vault",
      link: `/app/${url_name}/keys`,
      "data-testid": "vault-breadcrumb",
    },
    {
      name: "Key details",
      link: `/app/${url_name}/keys/details?keyName=${encodeURIComponent(keyName)}`,
    },
    {
      name: "Edit key",
    },
  ];

  return (
    <KeyPageWrapper>
      <KeyPageHeader
        keyName={keyName || ""}
        keyType={data?.credential?.credential_type || ""}
        breadcrumbsInformation={breadcrumbs}
      />
      <Card sx={{ padding: 4 }}>
        <EditorFormWithMutation
          onCompleted={({ name }) => onCompletedEditCredential(name || "")}
          credential={data?.credential}
          mode={"edit"}
        />
      </Card>
    </KeyPageWrapper>
  );
};

export { EditKey };
